/**
 * Cross-filter core (S11) — pure helpers between a chart mark click and the
 * per-tile execution request:
 *
 *   mark click ──markEventToFilters──▶ ActiveDashboardFilter[] (store, toggle)
 *   per tile   ──relevantFiltersForTile──▶ participation (binding match)
 *              ──toWireFilters──▶ WireDashboardFilter[] (typed values → main)
 *              ──injectFiltersSlot──▶ composedQuery for slot-less VQE tiles
 *
 * Values NEVER become SQL text here — predicates are composed main-side from
 * the wire filters (S10 typed-param seam). The only SQL rewriting the
 * renderer does is STRUCTURAL: inserting the literal `{{dashboard_filters}}`
 * slot via the resolver's add-where planner (G4).
 */
import type { ChartMarkEvent } from '../../charts/types/chartTypes';
import { buildParsedDocumentFromSource, getDocumentStructure } from '../../../sql-grammar/document';
import type { ParseOptions } from '../../../sql-grammar/model';
import type { ParsedBuffer } from '../../../sql-resolver/types';
import { applyStructuralEdit } from '../../../sql-resolver/edit/applyStructuralEdit';
import { emitSource } from '../../../sql-resolver/edit/emitSource';
import { extractQueryStructure } from '../../../sql-resolver/structure/extractQueryStructure';
// Dep-free leaf import — this module also runs in the electron main process
// (tileExecutionIpc), so it must not pull renderer schema-cache modules.
import { databaseEngineIdToSqlEngine } from '../../../../core/engines/databaseEngineIdToSqlEngine';
import type { DashboardTile, FilterBinding } from '../types/dashboardTypes';
import type { ActiveDashboardFilter, WireDashboardFilter } from '../types/dashboardRuntime';
import { DASHBOARD_FILTERS_NAME } from './parameterBinding';
import { findPlaceholders } from './sqlPlaceholders';
import { isQueryBackedTile } from '../types/dashboardTypes';
import type { QueryBackedTile } from './tileExecutionService';

export const DASHBOARD_FILTERS_SLOT_TEXT = `{{${DASHBOARD_FILTERS_NAME}}}`;

/** Chart "(null)" marks come from formatXValue(null) — map back to IS NULL. */
const NULL_MARK = '(null)';

export function bindingForField(
  tile: QueryBackedTile,
  dashboardField: string,
): FilterBinding | undefined {
  const wanted = dashboardField.toLowerCase();
  return tile.filterBindings?.find((b) => b.dashboardField.toLowerCase() === wanted);
}

/**
 * Where can this tile's predicate physically go? Slot in the SQL, or a
 * structural injection point (VQE/simple-SELECT). Memoized per (engine, sql)
 * — participation is consulted on every render and the injection probe
 * parses.
 */
const composableCache = new Map<string, boolean>();
export function canTileComposeFilters(tile: QueryBackedTile, engineId: string): boolean {
  if (hasLiveFiltersSlot(tile.source.sql, engineId)) return true;
  const key = `${engineId}\u0000${tile.source.sql}`;
  const cached = composableCache.get(key);
  if (cached !== undefined) return cached;
  const ok = injectFiltersSlot(tile.source.sql, engineId) !== null;
  if (composableCache.size > 512) composableCache.clear(); // session-bounded probe cache
  composableCache.set(key, ok);
  return ok;
}

/**
 * The filters THIS tile participates in: it must carry a matching binding
 * AND a place for the predicate to go (plan: a tile with no slot and no
 * seam stays unfiltered — and the chip count reflects that). A tile never
 * filters itself (the clicked segment would vanish — the source keeps
 * showing the full breakdown).
 */
export function relevantFiltersForTile(
  tile: QueryBackedTile,
  filters: ActiveDashboardFilter[],
  engineId: string,
): ActiveDashboardFilter[] {
  if (filters.length === 0) return filters;
  const mine = filters.filter(
    (f) => f.sourceTileId !== tile.id && bindingForField(tile, f.dashboardField),
  );
  if (mine.length === 0) return mine;
  return canTileComposeFilters(tile, engineId) ? mine : [];
}

/** Typed wire predicates for main-side composition — via the TILE's binding
 * (the target column and ITS type govern the bind). */
export function toWireFilters(
  tile: QueryBackedTile,
  filters: ActiveDashboardFilter[],
): WireDashboardFilter[] {
  const out: WireDashboardFilter[] = [];
  for (const filter of filters) {
    const binding = bindingForField(tile, filter.dashboardField);
    if (!binding) continue;
    out.push({
      targetExpression: binding.targetExpression,
      valueType: binding.valueType,
      value: filter.value === null ? null : String(filter.value),
    });
  }
  return out;
}

/** Chip copy — "filters N of M tiles" over the dashboard's query-backed tiles. */
export function crossFilterParticipation(
  tiles: DashboardTile[],
  filters: ActiveDashboardFilter[],
  engineOf: (tile: QueryBackedTile) => string,
): { filtered: number; total: number } {
  const queryBacked = tiles.filter(isQueryBackedTile);
  const filtered = queryBacked.filter(
    (t) => relevantFiltersForTile(t, filters, engineOf(t)).length > 0,
  ).length;
  return { filtered, total: queryBacked.length };
}

/**
 * A mark click on `tile` → the dashboard-level filters it implies. The
 * clicked x-field (and series field, when present) resolve through the
 * SOURCE tile's bindings to a logical dashboardField; an unbound source
 * column still emits under its own column name — sibling tiles match it
 * by THEIR bindings (or get offered wiring).
 */
export function markEventToFilters(
  tile: QueryBackedTile,
  event: ChartMarkEvent,
): ActiveDashboardFilter[] {
  const filters = [fieldFilter(tile, event.field, event.value)];
  if (event.seriesField && event.seriesValue !== undefined) {
    filters.push(fieldFilter(tile, event.seriesField, event.seriesValue));
  }
  return filters;
}

function fieldFilter(
  tile: QueryBackedTile,
  column: string,
  value: string | number | null,
): ActiveDashboardFilter {
  const lower = column.toLowerCase();
  const binding =
    tile.filterBindings?.find((b) => b.targetExpression.toLowerCase() === lower) ??
    tile.filterBindings?.find((b) => b.dashboardField.toLowerCase() === lower);
  const isNull = value === null || value === NULL_MARK;
  return {
    dashboardField: binding?.dashboardField ?? column,
    value: isNull ? null : value,
    valueType: binding?.valueType ?? (typeof value === 'number' ? 'number' : 'string'),
    sourceTileId: tile.id,
  };
}

const sameFilter = (a: ActiveDashboardFilter, b: ActiveDashboardFilter): boolean =>
  a.dashboardField.toLowerCase() === b.dashboardField.toLowerCase() && a.value === b.value;

/**
 * Click semantics: the exact same mark again CLEARS its fields; a different
 * mark REPLACES same-field filters and appends new fields (AND across
 * fields, single value per field).
 */
export function toggleFilters(
  existing: ActiveDashboardFilter[],
  incoming: ActiveDashboardFilter[],
): ActiveDashboardFilter[] {
  if (incoming.length === 0) return existing;
  const isRepeat = incoming.every((inc) => existing.some((e) => sameFilter(e, inc)));
  if (isRepeat) {
    return existing.filter((e) => !incoming.some((inc) => sameFilter(e, inc)));
  }
  const fields = new Set(incoming.map((i) => i.dashboardField.toLowerCase()));
  return [...existing.filter((e) => !fields.has(e.dashboardField.toLowerCase())), ...incoming];
}

// ---------------------------------------------------------------------------
// Slot injection — STRUCTURAL only (the literal {{dashboard_filters}} text).
// ---------------------------------------------------------------------------

/**
 * A slot only counts when the tokenizer sees it OUTSIDE comments and string
 * literals (C3 finding 4) — a commented-out slot is inert to composition, so
 * substring presence would make a tile "participate" while running
 * unfiltered.
 */
export function hasLiveFiltersSlot(sql: string, engineId: string): boolean {
  return findPlaceholders(sql, engineId).some((p) => p.name === DASHBOARD_FILTERS_NAME);
}

/**
 * KQL-aware scan (C3 finding 2): find the first top-level `| render` and the
 * first code character, skipping `//` line comments and '…'/"…" strings
 * (backslash escapes). Verbatim `@"…"` and multiline ``` strings are not
 * modeled (v1) — the post-injection live-slot check refuses those edge cases
 * rather than corrupting them.
 */
function scanKusto(sql: string): { firstCodeChar: string | null; renderIdx: number | null } {
  let firstCodeChar: string | null = null;
  let renderIdx: number | null = null;
  let i = 0;
  const n = sql.length;
  while (i < n) {
    const ch = sql[i];
    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }
    if (ch === '/' && sql[i + 1] === '/') {
      const nl = sql.indexOf('\n', i);
      i = nl === -1 ? n : nl + 1;
      continue;
    }
    if (firstCodeChar === null) firstCodeChar = ch;
    if (ch === '"' || ch === "'") {
      i += 1;
      while (i < n && sql[i] !== ch) {
        i += sql[i] === '\\' ? 2 : 1;
      }
      i += 1;
      continue;
    }
    if (ch === '|' && renderIdx === null && /^\s*render\b/i.test(sql.slice(i + 1))) {
      renderIdx = i;
    }
    i += 1;
  }
  return { firstCodeChar, renderIdx };
}

/**
 * Mirrors the kusto adapter's `splitKustoStatements` block rule: blank lines
 * separate statements; blocks whose every non-empty line is a `//` comment
 * don't count. (Multiline verbatim strings containing a blank line would
 * over-count — conservative: such tiles just don't auto-inject.)
 */
const countKustoBlocks = (sql: string): number => {
  let blocks = 0;
  let currentMeaningful = false;
  for (const line of sql.split('\n')) {
    const t = line.trim();
    if (t === '') {
      if (currentMeaningful) blocks += 1;
      currentMeaningful = false;
    } else if (!t.startsWith('//')) {
      currentMeaningful = true;
    }
  }
  if (currentMeaningful) blocks += 1;
  return blocks;
};

/**
 * Insert the `{{dashboard_filters}}` slot into a SQL statement via the
 * resolver's add-where planner (G4 seam — same machinery as VQE edits).
 * Returns null when the statement can't take a WHERE (not a plain SELECT,
 * parse failure, multi-statement) — callers fall back to asking the user to
 * place the slot manually. Every produced string is verified to carry a
 * LIVE slot before being returned.
 */
export function injectFiltersSlot(sql: string, engineId: string): string | null {
  if (engineId === 'kusto') {
    // C3-R2: the adapter executes blank-line-separated blocks as SEPARATE
    // statements/result sets (splitKustoStatements) — appending one `| where`
    // would filter only the LAST block while the tile may display another
    // (silent partially-unfiltered data). Multi-block buffers don't inject.
    if (countKustoBlocks(sql) !== 1) return null;
    const { firstCodeChar, renderIdx } = scanKusto(sql);
    if (firstCodeChar === null || firstCodeChar === '.') return null; // mgmt commands can't filter
    // `| render` must stay the last stage — insert the where before it.
    const injected =
      renderIdx !== null
        ? `${sql.slice(0, renderIdx)}| where ${DASHBOARD_FILTERS_SLOT_TEXT}\n${sql.slice(renderIdx)}`
        : `${sql.replace(/\s+$/, '')}\n| where ${DASHBOARD_FILTERS_SLOT_TEXT}`;
    return hasLiveFiltersSlot(injected, engineId) ? injected : null;
  }
  // The mock engine emits MSSQL-dialect SQL (e2e); unknown ids fail the map.
  const sqlEngine = engineId === 'mock' ? 'mssql' : databaseEngineIdToSqlEngine(engineId);
  if (!sqlEngine) return null;
  try {
    const options: ParseOptions = { engine: sqlEngine };
    const parsedDocument = buildParsedDocumentFromSource(sql, options);
    const documentStructure = getDocumentStructure(parsedDocument, 'dashboard-cross-filter');
    const buffer: ParsedBuffer = { parsedDocument, documentStructure };
    const statements = documentStructure.statements;
    if (statements.length !== 1 || statements[0].kind !== 'select') return null;
    // C3 finding 1: the planner's plain ` AND <slot>` append is wrong against
    // a top-level OR (`WHERE a OR b AND slot` ⇒ `a OR (b AND slot)` — silent
    // partially-unfiltered data). Parenthesize the existing predicate first.
    const where = extractQueryStructure(buffer, statements[0]).where ?? [];
    if (where.some((f) => f.leadingConnector === 'OR')) {
      const start = where[0].range.startOffset;
      const end = where[where.length - 1].range.endOffset;
      const wrapped = `${sql.slice(0, start)}(${sql.slice(start, end)}) AND ${DASHBOARD_FILTERS_SLOT_TEXT}${sql.slice(end)}`;
      return hasLiveFiltersSlot(wrapped, engineId) ? wrapped : null;
    }
    const modified = applyStructuralEdit(buffer, statements[0], {
      op: 'add-where',
      text: DASHBOARD_FILTERS_SLOT_TEXT,
      combinator: 'AND',
    });
    const injected = emitSource(modified, sql);
    return hasLiveFiltersSlot(injected, engineId) ? injected : null;
  } catch {
    return null;
  }
}
